import Vue from 'vue';
import {AXIOS} from "@/lib/http-common";
import Component from 'vue-class-component';
import {UserRoleWithPrefix} from "@/components/users/model/UserRoleWithPrefix";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import Client from "@/components/client/Client";
import License from "@/components/license/License";
import {v4 as uuidv4} from 'uuid';
import {LicenseStatus} from "@/components/license/LicenseStatus";

@Component({
    computed: {
        LicenseStatus() {
            return LicenseStatus
        },
        UserRoleWithPrefix() {
            return UserRoleWithPrefix;
        }
    }
})
export default class LicenseView extends Vue {
    public loading: boolean = false;
    public clients: Client[] = [];
    public newClient: Client = this.getInitialisedClient();
    public clientToEdit: Client = this.getInitialisedClient();
    public clientToDelete: Client = this.getInitialisedClient();
    public newLicense: License = this.getInitialisedLicense();
    public licenseToEdit: License = this.getInitialisedLicense();
    public addClientDialogOpened: boolean = false;
    public editClientDialogOpened: boolean = false;
    public deleteClientDialogOpened: boolean = false;
    public addLicenseDialogOpened: boolean = false;
    public editLicenseDialogOpened: boolean = false;
    public expandedRows: Client[] = [];
    public currentClient: Client | null = null;  // Track current client when adding license
    public singleExpand = true;

    get clientsTH() {
        return [
            {text: this.$t('clients.tableHeaders.name'), value: "name", width: "30%"},
            {text: this.$t('clients.tableHeaders.address'), value: "address", width: "30%"},
            {text: this.$t('clients.tableHeaders.licenses'), value: "data-table-expand", width: "30%"},
            {text: '', value: "action", sortable: false}

        ];
    }

    get licenseTH() {
        return [
            {text: this.$t('clients.tableHeaders.cubiscanModel'), value: "cubiscanModel"},
            {text: this.$t('clients.tableHeaders.licenseKey'), value: "licenseKey"},
            {text: this.$t('clients.tableHeaders.cubiscanSN'), value: "serialNumber"},
            {text: this.$t('clients.tableHeaders.licenseStatus'), value: "licenseStatus"},
      {text: this.$t('clients.tableHeaders.fileCheckRequired'), value: "fileCheckRequired"},
            {text: '', value: "action", sortable: false}
        ];
    }

    public getInitialisedClient(): Client {
        return {id: '', name: '', address: '', licenses: []};
    }

    public getInitialisedLicense(): License {
    return {licenseKey: '', cubiscanModel: '', serialNumber: '', macAddress:'', licenseStatus: '', clientId: '', fileCheckRequired: true};
    }

    public mounted() {
        this.getClientsFromBackend();
    }

    public getClientsFromBackend() {
        this.loading = true;
        AXIOS.get("/client")
            .then((message) => {
                this.clients = message.data;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public addClient() {
        AXIOS.post("/client", this.newClient)
            .then(() => {
                this.closeAddClientDialog();
                UserActionResultSender.showSuccessFeedback('users.success.userAdded');
            })
            .catch(error => {
                UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public editClient() {
        AXIOS.put("/client", this.clientToEdit)
            .then(() => {
                this.closeAddClientDialog();
                UserActionResultSender.showSuccessFeedback('users.success.userAdded');
            })
            .catch(error => {
                UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public deleteClient() {
        AXIOS.delete(`/client/${this.clientToDelete.id}`)
            .then(() => {
                this.closeAddClientDialog();
                UserActionResultSender.showSuccessFeedback('users.success.userAdded');
            })
            .catch(error => {
                UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    public addLicense() {
        if (this.currentClient) {
            this.newLicense.clientId = this.currentClient.id;
            AXIOS.post(`/license`, this.newLicense)
                .then(() => {
                    this.closeAddLicenseDialog();
                    UserActionResultSender.showSuccessFeedback('users.success.userAdded');
                })
                .catch(error => {
                    UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    public editLicense() {
        if (this.currentClient) {
            this.licenseToEdit.clientId = this.currentClient.id;
            AXIOS.put(`/license`, this.licenseToEdit)
                .then(() => {
                    this.closeEditLicenseDialog();
                    UserActionResultSender.showSuccessFeedback('users.success.userAdded');
                })
                .catch(error => {
                    UserActionResultSender.showErrorFeedback(error.response.data.errorCode);
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }

    public openAddClientDialog() {
        this.addClientDialogOpened = true;
    }

    public closeAddClientDialog() {
        this.newClient = this.getInitialisedClient();
        this.addClientDialogOpened = false;
        this.getClientsFromBackend();
    }

    public openAddLicenseDialog(client: Client) {
        this.currentClient = client;  // Track the client when the "Add License" button is clicked
        this.addLicenseDialogOpened = true;
    }

    public closeAddLicenseDialog() {
        this.newLicense = this.getInitialisedLicense();
        this.addLicenseDialogOpened = false;
        this.getClientsFromBackend();
    }

    public openEditLicenseDialog(license: License) {
        this.licenseToEdit = license;  // Track the client when the "Add License" button is clicked
        this.editLicenseDialogOpened = true;
    }

    public closeEditLicenseDialog() {
        this.licenseToEdit = this.getInitialisedLicense();
        this.editLicenseDialogOpened = false;
        this.getClientsFromBackend();
    }

    public openEditClientDialog(client: Client) {
        this.clientToEdit = client;
        this.editClientDialogOpened = true;
    }

    public closeEditClientDialog() {
        this.clientToEdit = this.getInitialisedClient();
        this.editClientDialogOpened = false;
        this.getClientsFromBackend();
    }

    public openDeleteClientDialog(client: Client) {
        this.clientToDelete = client;
        this.deleteClientDialogOpened = true;
    }

    public closeDeleteClientDialog() {
        this.clientToDelete = this.getInitialisedClient();
        this.deleteClientDialogOpened = false;
        this.getClientsFromBackend();
    }

    public toggleExpand(client: Client) {
        if (this.expandedRows.length === 1 && this.expandedRows[0].name === client.name) {
            this.expandedRows = [];
        } else {
            this.expandedRows = [client];
        }

        this.currentClient = client;
    }


    public isExpanded(client: Client): boolean {
        return this.expandedRows.some(c => c.name === client.name);
    }

    public generateNewLicenseKey() {
        this.newLicense.licenseKey = `INTHER_${uuidv4()}_CUBISCAN`;
    }

    public generateNewLicenseKeyForEdit() {
        this.licenseToEdit.licenseKey = `INTHER_${uuidv4()}_CUBISCAN`;
    }
}
